// packages/web/src/components/account/auth-signup-verify.tsx
/**
 * SignupVerify — stage 2 of two-stage signup (19BO+ Phase B).
 *
 * Opened by App root when the URL carries ?signup-token=… (the email link
 * points there). Calls /auth/signup/verify, which validates the token,
 * creates the user + grants signup bonus, and sets the session cookie. On
 * success we refresh currentUser from /auth/me (the cookie is now set) +
 * close the modal. On failure (invalid/expired/used token, or a concurrent
 * registration race) we show a retry prompt that routes back to signup.
 *
 * The token is consumed once (single-use). The URL param is cleared on
 * completion so a page refresh doesn't replay it.
 */

import { useEffect, useState } from 'react';
import { useT } from '../../i18n/use-t.js';
import { authApi } from '../../api/auth.js';
import { useStore } from '../../lib/store/index.js';
import { IngestApiError } from '../../lib/api-client.js';
import type { AuthMode } from './auth-modal.js';

interface Props {
  token: string;
  onSwitchMode: (m: AuthMode) => void;
  onClose: () => void;
}

type State = 'verifying' | 'success' | 'failed';

export function AuthSignupVerify({ token, onSwitchMode, onClose }: Props) {
  const t = useT();
  const [state, setState] = useState<State>('verifying');
  const refreshFromServer = useStore((s) => s.refreshFromServer);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await authApi.signupVerify(token);
        if (cancelled) return;
        // Cookie is now set; refresh currentUser from /auth/me (triggers the
        // login side effects — anon→email migration + notification).
        await refreshFromServer();
        if (cancelled) return;
        setState('success');
        // Clear the URL param so a refresh doesn't replay the (now-consumed)
        // token. Short delay so the success state is visible before close.
        clearSignupTokenParam();
        setTimeout(() => onClose(), 1200);
      } catch (err) {
        if (cancelled) return;
        setState('failed');
        clearSignupTokenParam();
        // 409 = concurrent registration race — different message than a
        // generic invalid token. Both route back to signup to retry.
        if (err instanceof IngestApiError && err.status === 409) {
          // keep 'failed' state; the UI offers "back to signup"
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- token is stable per mount
  }, [token]);

  if (state === 'verifying') {
    return (
      <div className="auth-body" style={{ textAlign: 'center', padding: 'var(--space-4xl) var(--space-3xl)' }}>
        <div className="auth-magic-sent-icon" style={{ background: 'var(--color-success-soft)', color: 'var(--color-accent)' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="26" height="26">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
        </div>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-ink)', margin: 'var(--space-xl) 0 0' }}>
          {t('auth.signupVerifyPending')}
        </h3>
      </div>
    );
  }

  if (state === 'success') {
    return (
      <div className="auth-body" style={{ textAlign: 'center', padding: 'var(--space-4xl) var(--space-3xl)' }}>
        <div className="auth-magic-sent-icon" style={{ background: 'var(--color-success-soft)', color: 'var(--color-success-deep)' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="26" height="26">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-ink)', margin: 'var(--space-xl) 0 0' }}>
          {t('auth.signupVerifySuccess')}
        </h3>
      </div>
    );
  }

  // state === 'failed'
  return (
    <div className="auth-body" style={{ textAlign: 'center', padding: 'var(--space-4xl) var(--space-3xl)' }}>
      <div className="auth-magic-sent-icon" style={{ background: 'var(--color-success-soft)', color: 'var(--color-accent)' }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="26" height="26">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      </div>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-ink)', margin: 'var(--space-xl) 0 0' }}>
        {t('auth.signupVerifyFailed')}
      </h3>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', margin: 'var(--space-md) 0 0' }}>
        {t('auth.signupVerifyFailedHint')}
      </p>
      <div className="auth-switch" style={{ marginTop: 'var(--space-2xl)' }}>
        <button type="button" onClick={() => onSwitchMode('signup')}>← {t('auth.signUp')}</button>
      </div>
    </div>
  );
}

/** Remove ?signup-token= from the URL without triggering a navigation. */
function clearSignupTokenParam(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete('signup-token');
  window.history.replaceState({}, '', url);
}
